import { Navigate, RouteObject, useParams } from "react-router-dom";
import { createDevelopmentLotSummaryUrl } from "src/RouteUrls";
import { NotFound } from "src/routes/NotFound";
import { DevelopmentLayout } from "src/routes/developments/DevelopmentLayout";
import { DevelopmentSideNavLayout } from "src/routes/developments/DevelopmentSideNavLayout";
import { AppShell } from "src/routing/components/AppShell";
import { GlobalNavLayout } from "src/routing/components/layouts/GlobalNavLayout";
import { changeLogSideNavRoutes } from "src/routing/domains/developments/change-log/changeLog";
import { developmentRoute } from "src/routing/domains/developments/detail/developmentDetailPaths";
import { fullPageUnderDevelopmentRoutes } from "src/routing/domains/developments/full-page/fullPage";
import { overviewSideNavRoutes } from "src/routing/domains/developments/overview/overview";
import { planAndOptionsSideNavRoutes } from "src/routing/domains/developments/plan-and-options/planAndOptions";
import { procurementSideNavRoutes } from "src/routing/domains/developments/procurement/procurement";
import { productOfferingsSideNavRoutes } from "src/routing/domains/developments/product-offerings/productOfferings";
import { schedulesSideNavRoutes } from "src/routing/domains/developments/schedules/schedules";
import { settingsSideNavRoutes } from "src/routing/domains/developments/settings/settings";
import { templatesSideNavRoutes } from "src/routing/domains/developments/templates/templates";

/**
 * `/developments/:developmentId/*` detail tree.
 *
 * Role: data router domain route.
 *
 * Stack (legacy chrome): `AppShell` → `GlobalNavLayout` → `DevelopmentLayout` →
 * `DevelopmentSideNavLayout` (SideNav + Outlet) → page. Full-page flows (edit-options,
 * review-templates, reassign-POs) are siblings under `DevelopmentLayout` with no SideNav and
 * no `ScrollableParentLayout` — they own scroll (`StepperProvider newLayout` or their own
 * `ScrollableParent`), matching legacy `PageLayout` `hasOwnLayout` for `/developments/:id`.
 *
 * Path constants live in subtree `*Paths.ts` / `developmentsPaths.ts`. Legacy `Routes.tsx` must not
 * register these paths.
 */

const developmentDetailChildren: RouteObject[] = [
  {
    Component: DevelopmentSideNavLayout,
    children: [
      { index: true, Component: RedirectToDevelopmentLotSummary },
      ...overviewSideNavRoutes,
      ...schedulesSideNavRoutes,
      ...planAndOptionsSideNavRoutes,
      ...productOfferingsSideNavRoutes,
      ...templatesSideNavRoutes,
      ...procurementSideNavRoutes,
      ...changeLogSideNavRoutes,
      ...settingsSideNavRoutes,
      { path: "*", Component: NotFound },
    ],
  },
  // Full-page development flows — keep `DevelopmentLayout` context, skip SideNav chrome.
  // Do not wrap in `ScrollableParentLayout` (pages own scroll; nesting breaks wizard height).
  ...fullPageUnderDevelopmentRoutes,
];
/**
 * Development detail tree without AppShell/GlobalNav — for unit tests and stories that render
 * `DevelopmentLayout` + SideNav leaves. Pass `appShell: true` to `render` / `setStory` so the
 * test helper owns `path` (same slot as `AppShell` in `developmentDetailMigratedRoute`).
 */
export const developmentDetailRoutes: RouteObject[] = [
  { path: developmentRoute.path, Component: DevelopmentLayout, children: developmentDetailChildren },
];

/** Production: AppShell owns the path → GlobalNav → pathless DevelopmentLayout → leaves. */
export const developmentDetailMigratedRoute: RouteObject = {
  path: developmentRoute.path,
  Component: AppShell,
  children: [
    { Component: GlobalNavLayout, children: [{ Component: DevelopmentLayout, children: developmentDetailChildren }] },
  ],
};

function RedirectToDevelopmentLotSummary() {
  const { developmentId } = useParams<{ developmentId: string }>();
  return <Navigate to={createDevelopmentLotSummaryUrl(developmentId!)} replace />;
}
